import { gql, useMutation } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import * as React from 'react';

import AdminSelectUser from './AdminSelectUser';

import type {
  AdminImpersonateUserMutation,
  AdminImpersonateUserMutationVariables,
} from './__generated__/AdminImpersonateUserPane.gql';

export const ADMIN_IMPERSONATE_USER = gql`
  mutation AdminImpersonateUser($input: AdminImpersonateUserInput!) {
    adminImpersonateUser(input: $input) {
      sessionId
    }
  }
`;

const AdminImpersonateUserPane = () => {
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const [adminImpersonateUser] = useMutation<AdminImpersonateUserMutation, AdminImpersonateUserMutationVariables>(
    ADMIN_IMPERSONATE_USER
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await adminImpersonateUser({
        variables: {
          input: { userId: selectedUserId },
        },
      });

      if (!result.data?.adminImpersonateUser?.sessionId) {
        throw new Error('Failed to create session');
      }

      setSuccess(true);

      // Redirect to home after a short delay
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserSelect = (userId: string | null) => {
    setSelectedUserId(userId);
    setError(null);
    setSuccess(false);
  };

  return (
    <div className="flex h-full w-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-4">
        <div className="text-3xl font-bold">Impersonate user</div>
        <div className="text-secondary text-sm">
          Impersonate another user by providing their User ID or Email address. This will create an admin session that
          expires in 30 minutes.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <AdminSelectUser
            selectedUserId={selectedUserId}
            onSelectionChange={handleUserSelect}
            className="w-full max-w-md"
          />
        </div>

        {error && <div className="bg-error-primary text-error-primary rounded-lg p-4 text-sm">{error}</div>}

        {success && (
          <div className="bg-success-primary text-success-primary rounded-lg p-4 text-sm">
            Successfully switched to user. Redirecting...
          </div>
        )}

        <div className="flex gap-4">
          <Button type="submit" isDisabled={isSubmitting || !selectedUserId}>
            {isSubmitting ? 'Switching...' : 'Impersonate user'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminImpersonateUserPane;
